import React from 'react';
import { i18nService } from '../services/i18n';
import BoclawLogo from '../components/icons/BoclawLogo';

// 跟随全站 accent 变量（组织品牌可覆盖），缺省回退到默认蓝
const BLUE = 'var(--accent-color, #3B82F6)';
const BLUE_TINT = 'color-mix(in srgb, var(--accent-color, #3B82F6) 12%, transparent)';
const cardShadow = '0 18px 44px rgba(28,48,98,.12), 0 4px 14px rgba(28,48,98,.06)';

interface AppDownloadViewProps {
  onBack: () => void;
  isMobile?: boolean;
}

const apple = (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" className="h-5 w-5">
    <path d="M16.4 12.7c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9s-1.8-.8-3-.8c-1.5 0-2.9.9-3.7 2.3-1.6 2.7-.4 6.8 1.1 9 .7 1.1 1.6 2.3 2.8 2.3 1.1 0 1.5-.7 2.9-.7s1.7.7 2.9.7 2-1.1 2.7-2.1c.8-1.2 1.2-2.4 1.2-2.5-.1 0-2.3-.9-2.3-3.5zM14.2 6.3c.6-.8 1-1.8.9-2.9-.9 0-2 .6-2.6 1.4-.6.7-1.1 1.7-.9 2.7 1 .1 2-.5 2.6-1.2z" />
  </svg>
);
const android = (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" className="h-5 w-5">
    <path d="M17.6 9.5 19 7a.4.4 0 1 0-.7-.4l-1.4 2.5A8.7 8.7 0 0 0 12 8c-1.7 0-3.3.4-4.9 1.1L5.7 6.6A.4.4 0 1 0 5 7l1.4 2.5A7.5 7.5 0 0 0 3 15.5h18a7.5 7.5 0 0 0-3.4-6zM8.2 13.2a.9.9 0 1 1 0-1.8.9.9 0 0 1 0 1.8zm7.6 0a.9.9 0 1 1 0-1.8.9.9 0 0 1 0 1.8z" />
  </svg>
);
const backIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
    <path d="m15 18-6-6 6-6" />
  </svg>
);
const dlIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
    <path d="M12 4v11m0 0 4-4m-4 4-4-4M5 20h14" />
  </svg>
);

const AppDownloadView: React.FC<AppDownloadViewProps> = ({ onBack }) => {
  return (
    <div className="flex-1 flex min-h-0 overflow-hidden bg-[#eef2fb] dark:bg-claude-darkBg p-2.5">
      <main
        className="flex flex-1 min-w-0 flex-col overflow-hidden rounded-[22px] bg-white dark:bg-claude-darkSurface"
        style={{ boxShadow: cardShadow }}
      >
        {/* 顶栏：返回 + 品牌 */}
        <div className="flex items-center gap-2 border-b border-black/[0.05] dark:border-white/10 px-4 py-2.5">
          <button
            type="button"
            onClick={onBack}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-claude-textSecondary hover:bg-claude-surfaceHover dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover"
            title={i18nService.t('back')}
          >
            {backIcon}
          </button>
          <BoclawLogo size={22} showText={false} />
          <span className="text-[15px] font-medium text-claude-text dark:text-claude-darkText">
            BoCoder <b className="font-bold" style={{ color: BLUE }}>App</b>
          </span>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-8">
          <div className="mx-auto max-w-4xl">
            {/* Hero */}
            <div className="flex flex-col items-center gap-3 text-center">
              <span className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium" style={{ color: BLUE, background: BLUE_TINT }}>
                移动端 · iOS &amp; Android
              </span>
              <h1 className="text-3xl font-bold leading-tight text-claude-text dark:text-claude-darkText">
                把 BoCoder 装进口袋
                <br />
                <span style={{ color: BLUE }}>随时协作 · 随时编程</span>
              </h1>
              <p className="max-w-xl text-sm text-claude-textSecondary dark:text-claude-darkTextSecondary">
                手机端与桌面端实时同步会话、定时任务与编程工作区。开会路上提个需求，到工位 Code 已经跑完。
              </p>
            </div>

            {/* 下载卡片 */}
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              <DownloadCard
                platIcon={apple}
                platBg="#111"
                name="iOS"
                meta="App Store · iOS 15+ · 48.6 MB"
                btn={<><span className="flex h-4 w-4 items-center justify-center">{apple}</span>App Store 下载</>}
                btnDark
                sub="v1.2.6 · 支持 TestFlight 内测"
              />
              <DownloadCard
                platIcon={android}
                platBg="#3ddc84"
                name="Android"
                meta="应用市场 / APK · Android 9+ · 42.3 MB"
                btn={<>{dlIcon}下载 APK</>}
                sub="华为 / 小米 / OPPO 应用市场可搜「BoCoder」"
              />
            </div>

            {/* 安装说明 */}
            <div className="mt-8 rounded-2xl border border-black/[0.06] dark:border-white/10 p-5">
              <h3 className="mb-4 text-base font-semibold text-claude-text dark:text-claude-darkText">安装说明</h3>
              <div className="grid gap-4 md:grid-cols-3">
                {[
                  ['1', '扫码或下载', '用手机相机扫描二维码，或点击对应平台按钮直接下载安装包。'],
                  ['2', '登录同一账号', '使用与桌面端相同的手机号 / 邮箱登录，会话与任务自动同步。'],
                  ['3', '开启通知', '允许推送，定时任务与 Code 续跑完成后第一时间收到提醒。'],
                ].map((s) => (
                  <div key={s[0]} className="flex flex-col gap-1.5">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: BLUE }}>
                      {s[0]}
                    </div>
                    <div className="text-sm font-semibold text-claude-text dark:text-claude-darkText">{s[1]}</div>
                    <div className="text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">{s[2]}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-1.5 border-t border-black/[0.06] dark:border-white/10 pt-4 text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">
                <span><b className="mr-2 text-claude-text dark:text-claude-darkText">系统要求</b>iOS 15.0+ / Android 9.0+</span>
                <span><b className="mr-2 text-claude-text dark:text-claude-darkText">当前版本</b>v1.2.6（2026-06）</span>
                <span><b className="mr-2 text-claude-text dark:text-claude-darkText">同步</b>会话 · 定时任务 · 编程工作区</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

function DownloadCard({
  platIcon, platBg, name, meta, btn, btnDark, sub,
}: {
  platIcon: React.ReactNode; platBg: string; name: string; meta: string;
  btn: React.ReactNode; btnDark?: boolean; sub: string;
}) {
  return (
    <div className="rounded-2xl border border-black/[0.06] dark:border-white/10 p-5">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl text-white" style={{ background: platBg }}>
          {platIcon}
        </div>
        <div>
          <div className="text-base font-semibold text-claude-text dark:text-claude-darkText">{name}</div>
          <div className="text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">{meta}</div>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-4">
        <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-xl border border-dashed border-black/15 dark:border-white/20 text-[11px] text-claude-textSecondary dark:text-claude-darkTextSecondary">
          扫码下载
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <button
            type="button"
            className="flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium text-white"
            style={{ background: btnDark ? '#111' : BLUE }}
          >
            {btn}
          </button>
          <div className="text-[11px] text-claude-textSecondary dark:text-claude-darkTextSecondary">{sub}</div>
        </div>
      </div>
    </div>
  );
}

export default AppDownloadView;
